import { ChannelStatus } from '../channel/channel-types';
import { PublisherEventConstants } from '../constants/event-constants';
import { MAX_SINGLE_MESSAGE_SIZE } from '../constants/value-constants';
import { chunkMessages } from '../utils/chunk-messages';
import { reconstructMessage } from '../utils/reconstruct-batched-message';
class Publisher {
    channelId;
    ttl;
    batchedMessageMap;
    pubnubInstance;
    getChannelStatus;
    onPubishFailed;
    onPublishSucces;
    logger;
    publisherQueue;
    inProcess;
    retryCount;
    sequence;
    sequenceStartTime;
    constructor(options) {
        this.channelId = options.channelId;
        this.ttl = options.ttl;
        this.sequence = options.initialSequence;
        this.sequenceStartTime = options.sequenceStartTime;
        this.pubnubInstance = options.pubnub;
        this.onPublishSucces = options.onPublishSucces;
        this.onPubishFailed = options.onPublishFailed;
        this.logger = options.logger;
        this.getChannelStatus = options.getChannelStatus;
        this.batchedMessageMap = {};
        this.publisherQueue = [];
        this.retryCount = 0;
        this.inProcess = false;
    }
    pushMessagesToBatchedQueue(message) {
        const strigifyiedMsg = JSON.stringify(message);
        // add code to split messages and push correct sequence to the batched queue
        const messageChunks = chunkMessages(strigifyiedMsg);
        const sequence = ++this.sequence;
        messageChunks.forEach((chunkedMessage, index) => {
            this.publisherQueue.push({
                payload: chunkedMessage,
                sequence,
                sequenceStartTime: this.sequenceStartTime,
                batchSequence: index + 1,
                batchSize: messageChunks.length,
            });
        });
        this.attemptPublish();
    }
    // create util to push to correct queue
    // create util to get next message topush
    // if message size less than 30KB - enqueue in queue and call attemptPublish
    // else  splits messages per 30 KB and push it to batchedQueue and call attemptPublish with batched flag
    publish(message) {
        if (JSON.stringify(message).length > MAX_SINGLE_MESSAGE_SIZE) {
            this.logger(PublisherEventConstants.PUBLISH_MESSAGE_OVER_MAX_SIZE, {
                size: JSON.stringify(message).length,
            });
            this.pushMessagesToBatchedQueue(message);
        }
        else {
            this.publisherQueue.push({
                payload: message,
                sequence: ++this.sequence,
                sequenceStartTime: this.sequenceStartTime,
            });
            this.attemptPublish();
        }
    }
    handleMessagePublish(message) {
        this.retryCount = 0;
        this.inProcess = false;
        this.publisherQueue.shift();
        if ('batchSize' in message) {
            if (message.batchSize === message.batchSequence) {
                const finalMessage = reconstructMessage([
                    ...(this.batchedMessageMap[message.sequence] || []),
                    message,
                ]);
                if (finalMessage) {
                    this.onPublishSucces(finalMessage);
                }
            }
            else if (message.batchSequence < message.batchSize) {
                this.batchedMessageMap[message.sequence] = [
                    ...(this.batchedMessageMap[message.sequence] || []),
                    message,
                ];
            }
        }
        else {
            this.onPublishSucces(message);
        }
        if (this.publisherQueue.length) {
            this.attemptPublish();
        }
    }
    handleMessageFailed(message, error) {
        this.inProcess = false;
        const channelStatus = this.getChannelStatus();
        if (this.retryCount < 2 && channelStatus === ChannelStatus.CONNECTED) {
            // TODO : Check pubnub error / network down m then wait till network up
            this.retryCount++;
            this.attemptPublish();
        }
        else {
            // call for failure
            this.onPubishFailed(error);
        }
    }
    // attempt to publish on pubnub instance with 2 retry
    // on success call handleSuccess update the sequence number
    // on final error call handleFailure
    async attemptPublish() {
        if (!this.inProcess &&
            this.publisherQueue.length &&
            this.getChannelStatus() === ChannelStatus.CONNECTED) {
            const message = this.publisherQueue[0];
            if (message) {
                this.inProcess = true;
                try {
                    this.logger(PublisherEventConstants.PUBLISH_POST_STARTED, {
                        sequence: message.sequence,
                        sequenceStartTime: this.sequenceStartTime,
                    });
                    await this.pubnubInstance.publish({
                        channel: this.channelId,
                        message: message,
                        storeInHistory: true,
                        ttl: this.ttl,
                    });
                    this.logger(PublisherEventConstants.PUBLISH_POST_SUCCESS, {
                        sequence: message.sequence,
                        sequenceStartTime: this.sequenceStartTime,
                        batchSequence: 'batchSequence' in message ? message.batchSequence : undefined,
                        batchSize: 'batchSequence' in message ? message.batchSequence : undefined,
                    });
                    this.handleMessagePublish(message);
                }
                catch (e) {
                    const error = e;
                    // POSSIBLE errors:
                    // JS execution failure
                    // PNNetworkIssuesCategory: not connected to pubnub for multiple reasons https://www.pubnub.com/docs/sdks/javascript/status-events#publish
                    // PNBadRequestCategory: Improper sdk initialisation, channel / message not present
                    const errorMessage = e instanceof Error ? e.message : error.category || JSON.stringify(error);
                    this.logger(PublisherEventConstants.PUBLISH_POST_FAILED, {
                        sequence: message.sequence,
                        sequenceStartTime: this.sequenceStartTime,
                        error: errorMessage,
                        status: error.statusCode,
                    });
                    this.handleMessageFailed(message, errorMessage);
                }
            }
        }
        if (this.inProcess) {
            this.logger(PublisherEventConstants.PUBLISH_ATTEMPTED_WHILE_IN_PROCESS, {
                sequence: this.sequence,
            });
        }
        return;
    }
}
export default Publisher;
//# sourceMappingURL=publisher.js.map